import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useProgramProgressSavedRefresh } from '../hooks/useProgramProgressSavedRefresh';
import { useProgramPostSaveDisplayHold, cloneProgramTableHoldPayload } from '../hooks/useProgramPostSaveDisplayHold';
import { useReleaseTrappedKeepAliveFocusWhenInactive } from '../utils/keepAliveFocusRelease';
import { ProgramProgressEmptyStateTableCard } from '../components/program/ProgramProgressEmptyStateTableCard';
import ProgramProgressTable, { type ProgramProgressUndoRedoApi } from '../components/program/ProgramProgressTable';
import ProgramTableDocumentExportButtons from '../components/program/ProgramTableDocumentExportButtons';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import ProgramWorkspaceStaleConflictBanner from '../components/program/ProgramWorkspaceStaleConflictBanner';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { useCalendarLinkedAcademicYear } from '../hooks/useCalendarLinkedAcademicYear';
import { useKeepAliveProgramTerm } from '../hooks/useKeepAliveProgramTerm';
import { useProgramProgressData } from '../hooks/useProgramProgressData';
import { useProgramFixedContentRows } from '../hooks/useProgramFixedContentRows';
import { useProgramWorkspaceScopeServerSync } from '../hooks/useProgramWorkspaceScopeServerSync';
import { useProgramProgressCells } from '../hooks/useProgramProgressCells';
import { useAnnualProjectProgramsResolved } from '../hooks/useAnnualProjectProgramsResolved';
import { deleteProgramProgressCellsByIds } from '../services/programProgressService';
import { fetchProgramFixedContentRowsHead } from '../services/programFixedContentService';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';
import { buildAnnualCellKey } from '../utils/annualTimetable';
import {
  buildProgramProgressRows,
  buildTrackNameByTimetableTextByGrade,
  buildWeeksFromRange,
  type ProgramProgressRow,
  resolveClassEntryByGradeId,
} from '../utils/programProgressRows';
import { buildGradeTrackTimetableContext } from '../utils/trackTimetableAlias';
import { buildWildcardCharsByGrade } from '../utils/trackWildcard';
import {
  buildProgramLearningContentExportFileBase,
  buildProgramDocumentScopeLabel,
  buildProgramNamedDocumentTitle,
  useProgramLearningContentDocumentExport,
} from '../hooks/useProgramLearningContentDocumentExport';
import {
  ProgramFixedContentRow,
  ProgramFixedContentRowUpsert,
  ProgramProgressCell,
} from '../types/domain';
import {
  ensureProgramWorkspaceScopeSnapshot,
  isProgramWorkspaceScopeDirty,
  getProgramWorkspaceScopeEntry,
  PROGRAM_WORKSPACE_CHANGED_EVENT,
  pruneProgramWorkspaceScopes,
  registerDiscardDirtyWorkspaceOnPageHide,
  removeProgramWorkspaceScopeEntry,
  resolveProgramWorkspacePersistKind,
  upsertProgramWorkspaceScopeEntry,
  type ProgramWorkspaceRowsChangeMeta,
} from '../utils/programEditWorkspace';
import {
  alignFixedContentRowToScheduleSlot,
  fixedSourceRichnessForEventQueue,
  getUnitTextAndNoteForDisplay,
  normalizeFixedQueueEventKey,
} from '../utils/programCellDisplayText';
import {
  computeProgramSavePending,
  discardAllProgramFamilyLeaveDrafts,
  saveProgramGradeTermWorkspaceAndAttachments,
} from '../utils/programGradeTermSave';
import { mergeProgramWorkspaceUpsertsIntoBaselineRows } from '../utils/programWorkspaceDisplayMerge';
import {
  PROGRAM_CONTENT_SLIDE_DRAFT_PREFIX,
  PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT,
} from '../services/programContentSlideService';
import {
  LEARNING_WORKSHEET_PENDING_PREFIX,
  LEARNING_WORKSHEET_PENDING_CHANGED_EVENT,
} from '../utils/learningContentWorksheetPendingStorage';
import { buildProgramProgressMondayIsoRangeForTerm } from '../utils/programProgressMondayRange';
import {
  filterOutSkipFixedTablePersistRows,
  fixedDbRowsToWorkspaceUpserts,
  isPristineMinimumProgramContentPlaceholderRows,
  normalizeFixedEventNameForWorkspace,
  programFixedWorkspaceRowsDigest,
  trimTrailingEmptyProgramContentOverflowRows,
  withMinimumProgramContentRowsForDisplay,
} from '../utils/programWorkspaceBaselineDigest';

type ProgramFixedPageProps = {
  isActive?: boolean;
};

/** 고정 프로그램 — 1/2학기 고정 시간표 입력 셀만 진도표 일정 생성 */
const ProgramFixedPage: React.FC<ProgramFixedPageProps> = ({ isActive = true }) => {
  const location = useLocation();
  useReleaseTrappedKeepAliveFocusWhenInactive(isActive);
  const normalizeEventName = React.useCallback((raw: string) => raw.trim().replace(/^◆+/, '').replace(/-+$/, '').trim(), []);
  const eventNameFromScheduleRow = React.useCallback(
    (rowMemo: string) => normalizeEventName(rowMemo ?? ''),
    [normalizeEventName]
  );
  /** memo가 비었거나 남아 있을 때 표시용 text_value(연간셀)로 이벤트 키를 잡아 일정·내용 불일치를 줄인다 */
  const eventNameFromScheduleSlot = React.useCallback(
    (row: { memo?: string; timetableText?: string }) => {
      const fromMemo = eventNameFromScheduleRow(row.memo ?? '');
      if (fromMemo) return fromMemo;
      return eventNameFromScheduleRow(row.timetableText ?? '');
    },
    [eventNameFromScheduleRow]
  );
  /** 일정·큐 매칭: 학습내용 `◆이벤트-` 접두를 단원/event_name보다 우선(단원만 바뀐 오래된 행과 분리 방지) */
  const eventNameFromContentRow = React.useCallback(
    (row: ProgramFixedContentRow) => {
      const fromLearn = (() => {
        const raw = (row.learning_text ?? '').replace(/\r\n/g, '\n').trim();
        if (!raw || raw.startsWith('{')) return '';
        const m = raw.match(/^◆\s*([^-]+?)\s*-/);
        return m?.[1] ? normalizeEventName(m[1]) : '';
      })();
      if (fromLearn) return fromLearn;
      const byEvent = normalizeEventName(row.event_name ?? '');
      if (byEvent) return byEvent;
      const byUnit = normalizeEventName(row.unit_text ?? '');
      if (byUnit) return byUnit;
      return '';
    },
    [normalizeEventName]
  );

  const [searchParams] = useSearchParams();
  const {
    programFocusGradeId,
    grades,
    workspaceId,
    setProgramFocusGradeId,
  } = useProgramSidebar();

  const gradeIdFromUrl = searchParams.get('gradeId');
  const focusAnnualCellKeyFromUrl = searchParams.get('focusAnnualCellKey')?.trim() ?? '';
  const effectiveGradeId = gradeIdFromUrl || programFocusGradeId || '';
  const programTermFromUrl = searchParams.get('programTerm');
  const termNumber = useKeepAliveProgramTerm(isActive, programTermFromUrl);
  const academicYear = useCalendarLinkedAcademicYear(workspaceId, effectiveGradeId.trim());
  const [workspaceStateRev, setWorkspaceStateRev] = React.useState(0);
  const [saveRefreshRev, setSaveRefreshRev] = React.useState(0);
  const bumpSaveRefreshRev = React.useCallback(() => setSaveRefreshRev((v) => v + 1), []);
  const bumpProgramSavePendingRevs = React.useCallback(() => {
    bumpSaveRefreshRev();
    setSlideDraftDirtyRev((v) => v + 1);
    setWorksheetDraftDirtyRev((v) => v + 1);
    setWorkspaceStateRev((v) => v + 1);
  }, [bumpSaveRefreshRev]);
  useProgramProgressSavedRefresh({
    workspaceId,
    gradeId: effectiveGradeId,
    academicYear,
    termNumber,
    onRefresh: bumpProgramSavePendingRevs,
  });
  const [programTableMountKey, setProgramTableMountKey] = React.useState(0);
  const programUndoRedoApiRef = React.useRef<ProgramProgressUndoRedoApi | null>(null);
  const [programUndoRedoAvail, setProgramUndoRedoAvail] = React.useState({ canUndo: false, canRedo: false });
  /** 수업 삭제로 숨긴 진도 찌꺼기 — 통합 저장 시에만 DB 삭제 */
  const [pendingMigrateProgressDeleteIds, setPendingMigrateProgressDeleteIds] = React.useState<string[]>([]);
  /** 저장 후 재조회가 끝날 때까지 방금 지운 legacy orphan 셀을 화면에서 숨긴다. */
  const [recentlyDeletedLegacyProgramCellIds, setRecentlyDeletedLegacyProgramCellIds] = React.useState<string[]>([]);
  void workspaceStateRev;

  const prevProgramTermRef = React.useRef<1 | 2 | null>(null);
  React.useEffect(() => {
    if (termNumber == null) {
      prevProgramTermRef.current = null;
      return;
    }
    if (prevProgramTermRef.current != null && prevProgramTermRef.current !== termNumber) {
      setProgramTableMountKey((k) => k + 1);
      setWorkspaceStateRev((v) => v + 1);
    }
    prevProgramTermRef.current = termNumber;
  }, [termNumber]);

  React.useEffect(() => {
    if (effectiveGradeId) setProgramFocusGradeId(effectiveGradeId);
  }, [effectiveGradeId, setProgramFocusGradeId]);

  const gradeRow = React.useMemo(
    () => (effectiveGradeId ? grades.find((g) => g.id === effectiveGradeId) ?? null : null),
    [effectiveGradeId, grades]
  );
  const classParsed = React.useMemo(
    () => (gradeRow ? parseGradeDisplayName(gradeRow.name) : null),
    [gradeRow]
  );

  const {
    loading,
    error,
    terms,
    schedule,
    tracks,
    gradeTrackSections,
    regularColors,
    dedicatedColors,
    baseExcludeColors,
    holidaySet,
    cellsByKey,
    schoolScopedCacheSyncReady,
  } = useProgramProgressData({
    workspaceId,
    grades,
    gradeId: effectiveGradeId,
    academicYear,
    termNumber: termNumber ?? 1,
    isActive,
  });
  const dataActive = isActive && schoolScopedCacheSyncReady;
  const selectedEntry = React.useMemo(
    () => resolveClassEntryByGradeId(schedule, grades, effectiveGradeId),
    [effectiveGradeId, grades, schedule]
  );
  const termDef = React.useMemo(
    () => (termNumber ? terms.find((term) => term.term_number === termNumber) ?? null : null),
    [termNumber, terms]
  );
  const weeks = React.useMemo(
    () => buildWeeksFromRange(termDef?.starts_on ?? '', termDef?.ends_on ?? ''),
    [termDef?.starts_on, termDef?.ends_on]
  );
  const programCellsMondayIsoRange = React.useMemo(() => {
    if (!termDef?.starts_on || !termDef?.ends_on) return null;
    return buildProgramProgressMondayIsoRangeForTerm(termDef.starts_on, termDef.ends_on);
  }, [termDef?.starts_on, termDef?.ends_on]);
  const trackNameByTimetableTextByGrade = React.useMemo(
    () => buildTrackNameByTimetableTextByGrade(tracks),
    [tracks]
  );
  const gradeTrackTimetableContext = React.useMemo(
    () => buildGradeTrackTimetableContext(workspaceId, effectiveGradeId, tracks, gradeTrackSections),
    [workspaceId, effectiveGradeId, tracks, gradeTrackSections]
  );
  const registeredTimetableNames = React.useMemo(() => {
    const set = new Set<string>();
    tracks.forEach((track) => {
      if ((track.grade_id ?? '').trim() !== effectiveGradeId) return;
      const base = (track.name ?? '').trim();
      const alias = (track.description ?? '').trim();
      if (base) set.add(base);
      if (alias) set.add(alias);
    });
    return Array.from(set);
  }, [effectiveGradeId, tracks]);
  const registeredTimetableWildcardChars = React.useMemo(() => {
    const byGrade = buildWildcardCharsByGrade(gradeTrackSections);
    return Array.from(byGrade[effectiveGradeId] ?? []);
  }, [effectiveGradeId, gradeTrackSections]);
  const annualProjectPrograms = useAnnualProjectProgramsResolved(workspaceId, effectiveGradeId, academicYear);

  const {
    loading: fixedRowsLoading,
    rows: fixedContentRows,
    saveSnapshotRows: saveFixedSnapshotRows,
  } = useProgramFixedContentRows({
    workspaceId,
    academicYear,
    termNumber,
    gradeId: effectiveGradeId,
    refreshNonce: saveRefreshRev,
    isActive: dataActive,
  });
  /** 일정에 없는 진도 셀 찌꺼기 → 고정 테이블로 옮길 때만 조회(단원·학습은 program_fixed_content_rows 가 단일 원천) */
  const { loading: programCellsLoading, cellsByAnnualKey: legacyProgramCellsByAnnualKey } = useProgramProgressCells({
    workspaceId,
    academicYear,
    termNumber,
    gradeId: effectiveGradeId,
    refreshKey: saveRefreshRev,
    mondayIsoRange: programCellsMondayIsoRange,
    deferFetchUntilMondayRange: true,
    isActive: dataActive,
  });
  const rows = React.useMemo(() => {
    if (!selectedEntry || !termDef || !termNumber) return [];
    const gradeRawName = grades.find((g) => g.id === selectedEntry.gradeId)?.name ?? selectedEntry.label;
    return buildProgramProgressRows({
      workspaceId,
      academicYear,
      termNumber,
      selectedEntry,
      gradeRawName,
      termDef,
      weeks,
      holidaySet,
      cellsByKey,
      dedicatedColors,
      baseExcludeColors,
      regularColors,
      annualProjectPrograms,
      trackNameByTimetableTextByGrade,
      gradeTrackTimetableContext,
      rowFilter: (input) => {
        const { isFixedProgram, isProjectProgram } = input;
        return isFixedProgram && !isProjectProgram;
      },
    });
  }, [
    academicYear,
    annualProjectPrograms,
    cellsByKey,
    dedicatedColors,
    baseExcludeColors,
    grades,
    holidaySet,
    regularColors,
    workspaceId,
    selectedEntry,
    termDef,
    termNumber,
    trackNameByTimetableTextByGrade,
    gradeTrackTimetableContext,
    weeks,
  ]);

  const tableLoading = loading || (fixedRowsLoading || programCellsLoading);
  React.useEffect(() => {
    if (!recentlyDeletedLegacyProgramCellIds.length || programCellsLoading) return;
    const liveIds = new Set(
      Object.values(legacyProgramCellsByAnnualKey)
        .map((cell) => (cell.id ?? '').trim())
        .filter(Boolean)
    );
    setRecentlyDeletedLegacyProgramCellIds((prev) => {
      const remaining = prev.filter((id) => liveIds.has(id));
      return remaining.length === prev.length ? prev : remaining;
    });
  }, [legacyProgramCellsByAnnualKey, programCellsLoading, recentlyDeletedLegacyProgramCellIds.length]);
  React.useEffect(() => {
    setRecentlyDeletedLegacyProgramCellIds([]);
  }, [academicYear, effectiveGradeId, workspaceId, termNumber]);
  const fixedScopeId = React.useMemo(
    () => `fixed|${workspaceId}|${effectiveGradeId}|${academicYear}|${termNumber}`,
    [academicYear, effectiveGradeId, workspaceId, termNumber]
  );
  const activeWorkspaceScopeId = React.useMemo(
    () => (workspaceId && effectiveGradeId && termNumber ? fixedScopeId : ''),
    [effectiveGradeId, fixedScopeId, workspaceId, termNumber]
  );
  const scheduleSlotAnnualCellKeys = React.useMemo(() => rows.map((r) => r.annualCellKey), [rows]);
  const fixedWorkspaceEntry = getProgramWorkspaceScopeEntry(fixedScopeId);
  const effectiveFixedContentRows = React.useMemo<ProgramFixedContentRow[]>(() => {
    if (!fixedWorkspaceEntry?.isDirty || !fixedWorkspaceEntry.rows?.length) return fixedContentRows;
    /** patch 시 LS에는 변경 행만 있을 수 있음 — DB baseline 위에 덮어써야 다른 행이 UI에서 비지 않는다 */
    const baselineSorted = [...fixedContentRows].sort((a, b) => a.order_index - b.order_index);
    const merged = mergeProgramWorkspaceUpsertsIntoBaselineRows(baselineSorted, fixedWorkspaceEntry.rows, {
      persistKind: fixedWorkspaceEntry.persistKind === 'replace' ? 'replace' : 'patch',
    });
    return merged.map((row, idx) => ({
      ...row,
      id: row.id ?? `virtual-${fixedScopeId}-${idx}`,
    })) as ProgramFixedContentRow[];
  }, [
    fixedContentRows,
    fixedScopeId,
    fixedWorkspaceEntry?.isDirty,
    fixedWorkspaceEntry?.persistKind,
    fixedWorkspaceEntry?.rows,
    workspaceStateRev,
  ]);
  const { tableRows, programCellsByAnnualKey, baselineWorkspaceUpserts } = React.useMemo(() => {
    const scheduleRows = rows;
    /** 연간 일정이 없어도 프로그램 내용 최소 1행은 편집 가능하게 표시 */
    if (!scheduleRows.length) {
      const contentSorted = [...effectiveFixedContentRows].sort((a, b) => a.order_index - b.order_index);
      const contentUpserts = contentSorted.map((row, idx) => ({
        ...row,
        order_index: idx,
      })) as ProgramFixedContentRowUpsert[];
      let padded =
        workspaceId && effectiveGradeId && termNumber
          ? withMinimumProgramContentRowsForDisplay(contentUpserts, {
              workspaceId,
              gradeId: effectiveGradeId,
              academicYear,
              termNumber,
            })
          : contentUpserts;
      /** dirty여도 DB/캐시 잔여 빈 넘침 행은 숨김(temp- 수업추가 행만 trim이 보존) */
      padded = trimTrailingEmptyProgramContentOverflowRows(padded);
      const mergedRows: ProgramProgressRow[] = padded.map((contentRow, orderIndex) => {
        const annualCellKey = `orphan:${contentRow.id ?? orderIndex}`;
        return {
          annualCellKey,
          gradeId: effectiveGradeId,
          mondayIso: '',
          weekdayKey: 'mon' as const,
          slotIndex: orderIndex,
          seq: orderIndex + 1,
          dateLabel: '',
          weekdayLabel: '',
          periodLabel: '',
          gradeLabel: '',
          semesterLabel: '',
          templateLabel: '',
          timetableText: '',
          timetableColorHex: '',
          timetableTextShrink: false,
          timetableSlotKind: 'out_of_term' as const,
          programKindLabel: '',
          memo: '',
          isScheduleAvailable: false,
          fixedContentId: contentRow.id,
          fixedOrderIndex: orderIndex,
          fixedSourceAnnualCellKey: contentRow.source_annual_cell_key ?? undefined,
          fixedEventName: eventNameFromContentRow(contentRow as unknown as ProgramFixedContentRow),
        };
      });
      const cellMap: Record<string, ProgramProgressCell> = {};
      mergedRows.forEach((row, idx) => {
        const src = padded[idx];
        if (!src) return;
        cellMap[row.annualCellKey] = {
          id: String(src.id ?? ''),
          workspace_id: src.workspace_id,
          grade_id: src.grade_id,
          academic_year: src.academic_year,
          term_number: src.term_number,
          monday_iso: row.mondayIso,
          weekday_key: row.weekdayKey,
          slot_index: row.slotIndex,
          timetable_text: row.timetableText ?? '',
          unit_text: src.unit_text ?? '',
          learning_text: src.learning_text ?? '',
          learning_note: src.learning_note ?? '',
          presentation_ref_id: src.presentation_ref_id ?? null,
          worksheet_ref_id: src.worksheet_ref_id ?? null,
          page_text: src.page_text ?? '',
          assistant_page_text: src.assistant_page_text ?? '',
          current_period_text: src.current_period_text ?? '',
          total_period_text: src.total_period_text ?? '',
          material_text: src.material_text ?? '',
          record_cross_curricular_text: src.record_cross_curricular_text ?? '',
          record_memo_text: src.record_memo_text ?? '',
          record_evaluation_text: src.record_evaluation_text ?? '',
          record_class_text: src.record_class_text ?? '',
          record_member_group_id: src.record_member_group_id ?? null,
          bg_color_by_field: src.bg_color_by_field ?? {},
        };
      });
      return {
        tableRows: mergedRows,
        programCellsByAnnualKey: cellMap,
        baselineWorkspaceUpserts: fixedDbRowsToWorkspaceUpserts(fixedContentRows),
      };
    }

    const buildModel = (sourcesInput: ProgramFixedContentRow[]) => {
    const sources = [...sourcesInput].sort((a, b) => a.order_index - b.order_index);
    const usedSourceIdx = new Set<number>();
    const n = scheduleRows.length;
    const slotContent: (ProgramFixedContentRow | null)[] = Array.from({ length: n }, () => null);
    const annualKeyToScheduleIndices = new Map<string, number[]>();
    scheduleRows.forEach((row, idx) => {
      const k = row.annualCellKey;
      const arr = annualKeyToScheduleIndices.get(k) ?? [];
      arr.push(idx);
      annualKeyToScheduleIndices.set(k, arr);
    });

    const pass1SourceOrder = sources.map((_, idx) => idx).sort((ia, ib) => {
      const ka = (sources[ia].source_annual_cell_key ?? '').trim();
      const kb = (sources[ib].source_annual_cell_key ?? '').trim();
      if (ka !== kb) return ia - ib;
      if (!ka) return ia - ib;
      const indicesList = annualKeyToScheduleIndices.get(ka);
      const slotIdx0 = indicesList != null && indicesList.length > 0 ? indicesList[0] : undefined;
      const ev =
        slotIdx0 !== undefined
          ? normalizeFixedQueueEventKey(eventNameFromScheduleSlot(scheduleRows[slotIdx0]))
          : '';
      const ra = fixedSourceRichnessForEventQueue(sources[ia], ev);
      const rb = fixedSourceRichnessForEventQueue(sources[ib], ev);
      if (rb !== ra) return rb - ra;
      return ia - ib;
    });

    pass1SourceOrder.forEach((srcIdx) => {
      const src = sources[srcIdx];
      const key = (src.source_annual_cell_key ?? '').trim();
      if (!key) return;
      const indices = annualKeyToScheduleIndices.get(key);
      if (!indices?.length) return;
      const j = indices.find((i) => slotContent[i] === null);
      if (j === undefined) return;
      slotContent[j] = src;
      usedSourceIdx.add(srcIdx);
    });

    /** pass1에서 새 셀 키만 맞는 빈 ◆이벤트- 행이 먼저 잡히는 경우, 같은 이벤트의 기존 본문 행으로 교체 */
    for (let si = 0; si < n; si += 1) {
      const cur = slotContent[si];
      if (!cur || String(cur.id ?? '').startsWith('virtual-')) continue;
      const scheduleEv = normalizeFixedQueueEventKey(
        eventNameFromScheduleSlot(scheduleRows[si]) || eventNameFromContentRow(cur)
      );
      if (!scheduleEv) continue;
      const curScore = fixedSourceRichnessForEventQueue(cur, scheduleEv);
      if (curScore > 100) continue;

      let bestIdx = -1;
      let bestScore = curScore;
      sources.forEach((s, idx) => {
        if (usedSourceIdx.has(idx)) return;
        if (normalizeFixedQueueEventKey(eventNameFromContentRow(s)) !== scheduleEv) return;
        const sc = fixedSourceRichnessForEventQueue(s, scheduleEv);
        if (sc > bestScore) {
          bestScore = sc;
          bestIdx = idx;
        }
      });
      if (bestIdx < 0) continue;
      const curIdx = sources.findIndex((s) => s === cur);
      if (curIdx < 0) continue;
      usedSourceIdx.delete(curIdx);
      usedSourceIdx.add(bestIdx);
      slotContent[si] = sources[bestIdx];
    }

    /** 일정에 없는 예전 source_annual_cell_key 행도 이벤트명으로 같은 접두 학습내용 매칭에 참여 */
    const unusedByEvent = new Map<string, number[]>();
    sources.forEach((src, srcIdx) => {
      if (usedSourceIdx.has(srcIdx)) return;
      const en = eventNameFromContentRow(src);
      if (!en) return;
      const list = unusedByEvent.get(en) ?? [];
      list.push(srcIdx);
      unusedByEvent.set(en, list);
    });

    unusedByEvent.forEach((list, eventKey) => {
      list.sort((ia, ib) => {
        const ra = fixedSourceRichnessForEventQueue(sources[ia], eventKey);
        const rb = fixedSourceRichnessForEventQueue(sources[ib], eventKey);
        if (rb !== ra) return rb - ra;
        return ia - ib;
      });
    });

    for (let i = 0; i < n; i += 1) {
      if (slotContent[i]) continue;
      const row = scheduleRows[i];
      const eventName = eventNameFromScheduleSlot(row);
      const queue = eventName ? unusedByEvent.get(eventName) : undefined;
      const sourceIdx = queue && queue.length > 0 ? queue.shift() ?? -1 : -1;
      if (sourceIdx >= 0) {
        usedSourceIdx.add(sourceIdx);
        slotContent[i] = sources[sourceIdx];
      } else {
        slotContent[i] = alignFixedContentRowToScheduleSlot(row.annualCellKey, eventName || '', {
          id: `virtual-${row.annualCellKey}`,
          workspace_id: workspaceId,
          grade_id: row.gradeId,
          academic_year: academicYear,
          term_number: termNumber ?? 1,
          order_index: i,
          source_annual_cell_key: row.annualCellKey,
          event_name: null,
          unit_text: '',
          learning_text: '',
          learning_note: '',
          presentation_ref_id: null,
          worksheet_ref_id: null,
          page_text: '',
          assistant_page_text: '',
          current_period_text: '',
          total_period_text: '',
          material_text: '',
          record_cross_curricular_text: '',
          record_memo_text: '',
          record_evaluation_text: '',
          record_class_text: '',
          bg_color_by_field: {},
        } as ProgramFixedContentRow);
      }
    }

    const aligned: ProgramFixedContentRow[] = slotContent as ProgramFixedContentRow[];
    for (let si = 0; si < n; si += 1) {
      const sch = scheduleRows[si];
      const cur = aligned[si];
      if (!sch || !cur) continue;
      aligned[si] = alignFixedContentRowToScheduleSlot(
        sch.annualCellKey,
        eventNameFromScheduleSlot(sch) || eventNameFromContentRow(cur),
        cur
      );
    }
    sources.forEach((src, idx) => {
      if (usedSourceIdx.has(idx)) return;
      aligned.push(src);
    });

    /** 일정에 없는 연간칸의 program_progress 찌꺼기(◆ 고정 형식) — 일정 행 아래에서 사용자가 정리 */
    const scheduleAnnualKeySet = new Set(scheduleRows.map((r) => r.annualCellKey));
    const coveredAnnualKeys = new Set(scheduleAnnualKeySet);
    aligned.forEach((r) => {
      const s = (r.source_annual_cell_key ?? '').trim();
      if (s) coveredAnnualKeys.add(s);
    });
    const wdOrder: Record<string, number> = { mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5 };
    const pendingMigrateDeleteSet = new Set([
      ...pendingMigrateProgressDeleteIds,
      ...recentlyDeletedLegacyProgramCellIds,
    ]);
    const legacyProgressOrphans = Object.values(legacyProgramCellsByAnnualKey).filter((cell) => {
      if (pendingMigrateDeleteSet.has(cell.id)) return false;
      const k = buildAnnualCellKey(cell.grade_id, cell.monday_iso, cell.weekday_key, cell.slot_index);
      if (coveredAnnualKeys.has(k)) return false;
      const u = (cell.unit_text ?? '').trim();
      const l = (cell.learning_text ?? '').trim();
      if (!u && !l) return false;
      if (!u.includes('◆') && !l.includes('◆')) return false;
      return true;
    });
    legacyProgressOrphans.sort((a, b) => {
      const c = a.monday_iso.localeCompare(b.monday_iso);
      if (c !== 0) return c;
      const wd = (wdOrder[a.weekday_key] ?? 9) - (wdOrder[b.weekday_key] ?? 9);
      if (wd !== 0) return wd;
      return a.slot_index - b.slot_index;
    });
    let tailOrder = aligned.length;
    for (const cell of legacyProgressOrphans) {
      const annualK = buildAnnualCellKey(cell.grade_id, cell.monday_iso, cell.weekday_key, cell.slot_index);
      aligned.push({
        id: `virtual-migrate-${cell.id}`,
        workspace_id: cell.workspace_id,
        grade_id: cell.grade_id,
        academic_year: cell.academic_year,
        term_number: cell.term_number,
        order_index: tailOrder,
        source_annual_cell_key: annualK,
        event_name: null,
        unit_text: cell.unit_text ?? '',
        learning_text: cell.learning_text ?? '',
        learning_note: cell.learning_note ?? '',
        presentation_ref_id: cell.presentation_ref_id ?? null,
        worksheet_ref_id: cell.worksheet_ref_id ?? null,
        page_text: cell.page_text ?? '',
        assistant_page_text: cell.assistant_page_text ?? '',
        current_period_text: cell.current_period_text ?? '',
        total_period_text: cell.total_period_text ?? '',
        material_text: cell.material_text ?? '',
        record_cross_curricular_text: cell.record_cross_curricular_text ?? '',
        record_memo_text: cell.record_memo_text ?? '',
        record_evaluation_text: cell.record_evaluation_text ?? '',
        record_class_text: cell.record_class_text ?? '',
        bg_color_by_field: cell.bg_color_by_field ?? {},
      } as ProgramFixedContentRow);
      tailOrder += 1;
    }

    const mergedRows = aligned.map((contentRow, idx) => {
      const scheduleRow = scheduleRows[idx];
      if (scheduleRow) {
        const contentRowId = String(contentRow.id ?? '');
        return {
          ...scheduleRow,
          isScheduleAvailable: true,
          fixedContentId: contentRowId.startsWith('virtual-') ? undefined : contentRowId || undefined,
          fixedOrderIndex: idx,
          fixedSourceAnnualCellKey: scheduleRow.annualCellKey,
          fixedEventName: eventNameFromScheduleSlot(scheduleRow) || eventNameFromContentRow(contentRow),
        };
      }
      // 일정과 매칭되지 않은 편집 행은 항상 고유 orphan 키를 사용해 충돌/덮어쓰기 방지
      const migrateId = String(contentRow.id ?? '').startsWith('virtual-migrate-')
        ? String(contentRow.id).replace(/^virtual-migrate-/, '')
        : '';
      const syntheticKey = migrateId ? `orphan:legacy-${migrateId}` : `orphan:${contentRow.id || idx}`;
      const eventName = eventNameFromContentRow(contentRow);
      return {
        annualCellKey: syntheticKey,
        gradeId: effectiveGradeId,
        mondayIso: '',
        weekdayKey: 'mon' as const,
        slotIndex: idx,
        seq: idx + 1,
        dateLabel: '',
        weekdayLabel: '',
        periodLabel: '',
        gradeLabel: '',
        semesterLabel: '',
        templateLabel: '',
        timetableText: '',
        timetableColorHex: '',
        timetableTextShrink: false,
        timetableSlotKind: 'out_of_term' as const,
        programKindLabel: '',
        memo: '',
        isScheduleAvailable: false,
        fixedContentId: contentRow.id,
        fixedOrderIndex: idx,
        fixedSourceAnnualCellKey: contentRow.source_annual_cell_key ?? undefined,
        fixedEventName: eventName,
      };
    });

    const cellMap: Record<string, ProgramProgressCell> = {};
    mergedRows.forEach((row, idx) => {
      const src = aligned[idx];
      if (!src) return;
      const sid = String(src.id ?? '');
      const draftCellId = sid.startsWith('virtual-migrate-') ? sid.replace(/^virtual-migrate-/, '') : sid;
      cellMap[row.annualCellKey] = {
        id: draftCellId,
        workspace_id: src.workspace_id,
        grade_id: src.grade_id,
        academic_year: src.academic_year,
        term_number: src.term_number,
        monday_iso: row.mondayIso,
        weekday_key: row.weekdayKey,
        slot_index: row.slotIndex,
        timetable_text: row.timetableText ?? '',
        unit_text: src.unit_text ?? '',
        learning_text: src.learning_text ?? '',
        learning_note: src.learning_note ?? '',
        presentation_ref_id: src.presentation_ref_id ?? null,
        worksheet_ref_id: src.worksheet_ref_id ?? null,
        page_text: src.page_text ?? '',
        assistant_page_text: src.assistant_page_text ?? '',
        current_period_text: src.current_period_text ?? '',
        total_period_text: src.total_period_text ?? '',
        material_text: src.material_text ?? '',
        record_cross_curricular_text: src.record_cross_curricular_text ?? '',
        record_memo_text: src.record_memo_text ?? '',
        record_evaluation_text: src.record_evaluation_text ?? '',
        record_class_text: src.record_class_text ?? '',
        record_member_group_id: src.record_member_group_id ?? null,
        bg_color_by_field: src.bg_color_by_field ?? {},
      };
    });

    const alignedWorkspaceUpserts: ProgramFixedContentRowUpsert[] = mergedRows.map((mrow, orderIndex) => {
      const src = aligned[orderIndex];
      const bg: Record<string, string> = { ...((src.bg_color_by_field ?? {}) as Record<string, string>) };
      const eventName = normalizeFixedEventNameForWorkspace(
        (mrow.memo || mrow.fixedEventName || getUnitTextAndNoteForDisplay(src.unit_text ?? '').text || src.learning_text || '').trim()
      );
      return {
        id: src.id?.startsWith('virtual-') ? undefined : src.id,
        workspace_id: workspaceId,
        grade_id: src.grade_id,
        academic_year: academicYear,
        term_number: termNumber ?? 1,
        order_index: orderIndex,
        source_annual_cell_key: src.source_annual_cell_key ?? (mrow.isScheduleAvailable === false ? null : mrow.annualCellKey),
        event_name: eventName || null,
        unit_text: src.unit_text ?? '',
        learning_text: src.learning_text ?? '',
        learning_note: src.learning_note ?? '',
        presentation_ref_id: src.presentation_ref_id ?? null,
        worksheet_ref_id: src.worksheet_ref_id ?? null,
        page_text: src.page_text ?? '',
        assistant_page_text: src.assistant_page_text ?? '',
        current_period_text: src.current_period_text ?? '',
        total_period_text: src.total_period_text ?? '',
        material_text: src.material_text ?? '',
        record_cross_curricular_text: src.record_cross_curricular_text ?? '',
        record_memo_text: src.record_memo_text ?? '',
        record_evaluation_text: src.record_evaluation_text ?? '',
        record_class_text: src.record_class_text ?? '',
        record_member_group_id: src.record_member_group_id ?? null,
        bg_color_by_field: bg,
      };
    });

    return { tableRows: mergedRows, programCellsByAnnualKey: cellMap, alignedWorkspaceUpserts };
    };

    const baselineBuilt = buildModel(fixedContentRows);
    const displayBuilt = buildModel(effectiveFixedContentRows);
    return {
      tableRows: displayBuilt.tableRows,
      programCellsByAnnualKey: displayBuilt.programCellsByAnnualKey,
      baselineWorkspaceUpserts: baselineBuilt.alignedWorkspaceUpserts,
    };
  }, [
    academicYear,
    effectiveGradeId,
    eventNameFromContentRow,
    eventNameFromScheduleSlot,
    effectiveFixedContentRows,
    fixedContentRows,
    legacyProgramCellsByAnnualKey,
    alignFixedContentRowToScheduleSlot,
    rows,
    workspaceId,
    termNumber,
    pendingMigrateProgressDeleteIds,
    recentlyDeletedLegacyProgramCellIds,
    fixedWorkspaceEntry?.isDirty,
  ]);
  const liveProgramTableDisplay = React.useMemo(
    () => ({
      tableRows,
      programCellsByAnnualKey,
      scheduleSlotAnnualCellKeys,
    }),
    [programCellsByAnnualKey, scheduleSlotAnnualCellKeys, tableRows]
  );
  const {
    display: heldProgramTableDisplay,
    holding: postSaveHolding,
    beginHold,
    endHold,
  } = useProgramPostSaveDisplayHold(liveProgramTableDisplay);
  const displayTableRows = heldProgramTableDisplay.tableRows;
  const displayProgramCellsByAnnualKey = heldProgramTableDisplay.programCellsByAnnualKey;
  const displayScheduleSlotAnnualCellKeys = heldProgramTableDisplay.scheduleSlotAnnualCellKeys;
  const hasDisplayTableContent = displayTableRows.length > 0 || rows.length > 0 || fixedContentRows.length > 0;
  const showTableLoading = tableLoading && !postSaveHolding;

  const fixedDocumentExportTitle = React.useMemo(
    () => buildProgramNamedDocumentTitle('고정 프로그램'),
    []
  );

  const fixedDocumentScopeLabel = React.useMemo(
    () => buildProgramDocumentScopeLabel(termNumber ?? 1),
    [termNumber]
  );

  const fixedDocumentExportFileBase = React.useMemo(
    () =>
      buildProgramLearningContentExportFileBase({
        scopeLabel: classParsed?.title ?? '클래스',
        programLabel: '고정프로그램',
        termNumber: termNumber ?? 1,
      }),
    [classParsed?.title, termNumber]
  );

  const {
    canExport: canExportProgramDocument,
    exportBusy: programDocumentExportBusy,
    handleSaveExcel: handleProgramDocumentSaveExcel,
    handleSavePdf: handleProgramDocumentSavePdf,
    handlePrint: handleProgramDocumentPrint,
  } = useProgramLearningContentDocumentExport({
    tableRows: displayTableRows,
    programCellsByAnnualKey: displayProgramCellsByAnnualKey,
    scopeLabel: fixedDocumentScopeLabel,
    programTitle: fixedDocumentExportTitle,
    scopeBadge: termNumber === 2 ? 'term2' : 'term1',
    fileNameBase: fixedDocumentExportFileBase,
  });

  const upsertCells = React.useCallback(
    async (upserts: ProgramFixedContentRowUpsert[]) => {
      const migrateProgressIds = upserts
        .map((r) => r.id)
        .filter((id): id is string => typeof id === 'string' && id.startsWith('virtual-migrate-'))
        .map((id) => id.replace(/^virtual-migrate-/, ''));
      const persistable = filterOutSkipFixedTablePersistRows(upserts).map((row) => ({
        ...row,
        id: row.id?.startsWith('virtual-') ? undefined : row.id,
      }));
      if (!persistable.length && !migrateProgressIds.length) return;
      if (persistable.length) await saveFixedSnapshotRows(persistable);
      if (migrateProgressIds.length) await deleteProgramProgressCellsByIds(migrateProgressIds);
    },
    [saveFixedSnapshotRows]
  );

  const [saveAction, setSaveAction] = React.useState<(() => Promise<void>) | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [saveToastState, setSaveToastState] = React.useState<
    | {
        kind: 'saving' | 'success' | 'error';
        message: string;
      }
    | null
  >(null);
  const [slideDraftDirtyRev, setSlideDraftDirtyRev] = React.useState(0);
  const [worksheetDraftDirtyRev, setWorksheetDraftDirtyRev] = React.useState(0);
  const handleBindSaveAction = React.useCallback((fn: (() => Promise<void>) | null) => {
    setSaveAction(() => fn ?? null);
  }, []);
  const { setProgramProgressDirty, saveProgramProgressRef } = React.useContext(GradeTrackLeaveContext);
  React.useEffect(() => {
    const refresh = () => setWorkspaceStateRev((v) => v + 1);
    window.addEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, refresh as EventListener);
    return () => window.removeEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, refresh as EventListener);
  }, []);
  React.useEffect(() => {
    if (!activeWorkspaceScopeId) return;
    pruneProgramWorkspaceScopes([activeWorkspaceScopeId]);
  }, [activeWorkspaceScopeId]);
  React.useEffect(() => {
    if (!fixedScopeId || fixedContentRows.length === 0) return;
    const ent = getProgramWorkspaceScopeEntry(fixedScopeId);
    if (!ent?.isDirty || !ent.rows?.length) return;
    if (ent.rows.length >= fixedContentRows.length) return;
    if (!isPristineMinimumProgramContentPlaceholderRows(ent.rows)) return;
    removeProgramWorkspaceScopeEntry(fixedScopeId);
  }, [fixedContentRows.length, fixedScopeId]);
  React.useEffect(() => {
    if (!activeWorkspaceScopeId) return () => {};
    return registerDiscardDirtyWorkspaceOnPageHide(activeWorkspaceScopeId);
  }, [activeWorkspaceScopeId]);
  const handleWorkspaceRowsChange = React.useCallback(
    (rowsFromTable: ProgramFixedContentRowUpsert[] | null, meta?: ProgramWorkspaceRowsChangeMeta) => {
      if (!effectiveGradeId || !termNumber) return;
      if (rowsFromTable == null) {
        removeProgramWorkspaceScopeEntry(fixedScopeId);
        return;
      }
      if (isPristineMinimumProgramContentPlaceholderRows(rowsFromTable)) {
        removeProgramWorkspaceScopeEntry(fixedScopeId);
        return;
      }
      const baseline = baselineWorkspaceUpserts;
      const persistableFromTable = filterOutSkipFixedTablePersistRows(rowsFromTable);
      const dirty =
        baseline.length !== persistableFromTable.length ||
        programFixedWorkspaceRowsDigest(rowsFromTable) !== programFixedWorkspaceRowsDigest(baseline);
      upsertProgramWorkspaceScopeEntry({
        scopeId: fixedScopeId,
        scopeType: 'fixed',
        scopeKey: 'fixed',
        workspaceId,
        gradeId: effectiveGradeId,
        academicYear,
        termNumber,
        rows: rowsFromTable,
        isDirty: dirty,
        updatedAt: Date.now(),
        persistKind: resolveProgramWorkspacePersistKind(meta, rowsFromTable.length, baseline.length),
      });
    },
    [academicYear, baselineWorkspaceUpserts, effectiveGradeId, fixedScopeId, workspaceId, termNumber]
  );
  const handleEnsureWorkspaceSnapshot = React.useCallback(
    (rowsFromTable: ProgramFixedContentRowUpsert[]) => {
      if (!effectiveGradeId || !termNumber || !rowsFromTable.length) return;
      /** UI용 최소 1행 가짜 스냅샷은 LS에 굳히지 않음 */
      if (isPristineMinimumProgramContentPlaceholderRows(rowsFromTable)) return;
      ensureProgramWorkspaceScopeSnapshot({
        scopeId: fixedScopeId,
        scopeType: 'fixed',
        scopeKey: 'fixed',
        workspaceId,
        gradeId: effectiveGradeId,
        academicYear,
        termNumber,
        rows: rowsFromTable,
        isDirty: false,
        updatedAt: Date.now(),
      });
    },
    [academicYear, effectiveGradeId, fixedScopeId, workspaceId, termNumber]
  );
  /** 수업 삭제: DB는 통합 저장 시 — 여기서는 찌꺼기 재부착 방지용 대기 목록만 둔다 */
  const handleRemovedFixedWorkspaceRows = React.useCallback((removed: ProgramFixedContentRowUpsert[]) => {
    const scheduleAnnualKeySet = new Set(scheduleSlotAnnualCellKeys);
    const migrateIds = new Set<string>();
    removed.forEach((row) => {
      const rowId = (row.id ?? '').trim();
      if (rowId.startsWith('virtual-migrate-')) {
        migrateIds.add(rowId.replace(/^virtual-migrate-/, ''));
      }
      const sourceAnnualKey = (row.source_annual_cell_key ?? '').trim();
      if (!sourceAnnualKey || scheduleAnnualKeySet.has(sourceAnnualKey)) return;
      const legacy = legacyProgramCellsByAnnualKey[sourceAnnualKey];
      const legacyId = (legacy?.id ?? '').trim();
      if (legacyId) migrateIds.add(legacyId);
    });
    if (migrateIds.size === 0) return;
    setPendingMigrateProgressDeleteIds((prev) => {
      const next = new Set(prev);
      migrateIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, [legacyProgramCellsByAnnualKey, scheduleSlotAnnualCellKeys]);
  React.useEffect(() => {
    if (!saveToastState || saveToastState.kind === 'saving') return;
    const t = window.setTimeout(() => setSaveToastState(null), 1800);
    return () => window.clearTimeout(t);
  }, [saveToastState]);
  const handleSave = React.useCallback(async () => {
    setSaveToastState({ kind: 'saving', message: '저장 중입니다...' });
    beginHold(
      cloneProgramTableHoldPayload({
        tableRows: displayTableRows,
        programCellsByAnnualKey: displayProgramCellsByAnnualKey,
        scheduleSlotAnnualCellKeys: displayScheduleSlotAnnualCellKeys,
      })
    );
    const migrateDeletesSnapshot = [...pendingMigrateProgressDeleteIds];
    try {
      const workspaceDirty = isProgramWorkspaceScopeDirty(fixedScopeId);
      const shouldRunTableSave = !!(saveAction && (hasUnsavedChanges || workspaceDirty));
      if (shouldRunTableSave) await saveAction!();
      /**
       * 현재 고정 표(saveAction)는 이미 DB 저장을 끝냈다.
       * 통합 저장에서 같은 scope를 다시 저장하면 stale 워크스페이스 스냅샷이 replace로 적용될 수 있어 제외한다.
       */
      let uiRefreshMode: 'full' | 'cache' = 'full';
      if (effectiveGradeId && termNumber) {
        const result = await saveProgramGradeTermWorkspaceAndAttachments(
          {
            workspaceId,
            gradeId: effectiveGradeId,
            academicYear,
            termNumber,
          },
          { excludeWorkspaceScopeId: shouldRunTableSave ? fixedScopeId : undefined }
        );
        uiRefreshMode = result.uiRefreshMode;
      }
      if (migrateDeletesSnapshot.length) {
        setRecentlyDeletedLegacyProgramCellIds((prev) =>
          Array.from(new Set([...prev, ...migrateDeletesSnapshot]))
        );
        await deleteProgramProgressCellsByIds(migrateDeletesSnapshot);
        setPendingMigrateProgressDeleteIds((prev) =>
          prev.filter((id) => !migrateDeletesSnapshot.includes(id))
        );
        uiRefreshMode = 'full';
      }
      setHasUnsavedChanges(false);
      setSlideDraftDirtyRev((v) => v + 1);
      setWorksheetDraftDirtyRev((v) => v + 1);
      setWorkspaceStateRev((v) => v + 1);
      if (uiRefreshMode === 'full') {
        setSaveRefreshRev((v) => v + 1);
      }
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      endHold();
      setSaveToastState({ kind: 'success', message: '저장되었습니다.' });
    } catch (e) {
      endHold();
      setSaveToastState({
        kind: 'error',
        message: e instanceof Error ? e.message : '저장에 실패했습니다.',
      });
    }
  }, [
    academicYear,
    beginHold,
    displayProgramCellsByAnnualKey,
    displayScheduleSlotAnnualCellKeys,
    displayTableRows,
    effectiveGradeId,
    endHold,
    fixedScopeId,
    hasUnsavedChanges,
    pendingMigrateProgressDeleteIds,
    saveAction,
    workspaceId,
    termNumber,
  ]);

  const handleDiscardUnsaved = React.useCallback(() => {
    discardAllProgramFamilyLeaveDrafts();
    setPendingMigrateProgressDeleteIds([]);
    setRecentlyDeletedLegacyProgramCellIds([]);
    endHold();
    setProgramTableMountKey((k) => k + 1);
    setSaveRefreshRev((v) => v + 1);
    setWorkspaceStateRev((v) => v + 1);
    setSlideDraftDirtyRev((v) => v + 1);
    setWorksheetDraftDirtyRev((v) => v + 1);
    setHasUnsavedChanges(false);
  }, [endHold]);

  const pageWorkspaceDirty = isProgramWorkspaceScopeDirty(fixedScopeId);
  const fixedScopeServerSync = useProgramWorkspaceScopeServerSync({
    scopeId: fixedScopeId,
    enabled: dataActive && !!fixedScopeId && !!effectiveGradeId && !!termNumber,
    serverRows: fixedContentRows,
    fetchHead: React.useCallback(
      () =>
        fetchProgramFixedContentRowsHead(
          workspaceId,
          academicYear,
          termNumber as 1 | 2,
          effectiveGradeId
        ),
      [academicYear, effectiveGradeId, termNumber, workspaceId]
    ),
    onAutoRefresh: React.useCallback(() => setSaveRefreshRev((v) => v + 1), []),
  });
  const programGradeTermSaveTarget = React.useMemo(
    () => ({
      workspaceId,
      gradeId: effectiveGradeId,
      academicYear,
      termNumber: (termNumber ?? 1) as 1 | 2,
    }),
    [academicYear, effectiveGradeId, termNumber, workspaceId]
  );
  const leavePending =
    pageWorkspaceDirty ||
    (hasUnsavedChanges && saveAction != null) ||
    pendingMigrateProgressDeleteIds.length > 0;
  const programSavePending = React.useMemo(() => {
    void workspaceStateRev;
    void slideDraftDirtyRev;
    void worksheetDraftDirtyRev;
    if (pendingMigrateProgressDeleteIds.length > 0) return true;
    if (!effectiveGradeId || !termNumber) return false;
    return computeProgramSavePending({
      target: programGradeTermSaveTarget,
      hasUnsavedTableChanges: hasUnsavedChanges,
      hasTableSaveAction: !!saveAction,
    });
  }, [
    effectiveGradeId,
    hasUnsavedChanges,
    pendingMigrateProgressDeleteIds.length,
    programGradeTermSaveTarget,
    saveAction,
    slideDraftDirtyRev,
    termNumber,
    worksheetDraftDirtyRev,
    workspaceStateRev,
  ]);
  const programLeaveDirty = pageWorkspaceDirty || leavePending;

  React.useEffect(() => {
    const syncSlides = () => setSlideDraftDirtyRev((v) => v + 1);
    const syncWorksheets = () => setWorksheetDraftDirtyRev((v) => v + 1);
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key.startsWith(PROGRAM_CONTENT_SLIDE_DRAFT_PREFIX)) syncSlides();
      if (e.key.startsWith(LEARNING_WORKSHEET_PENDING_PREFIX)) syncWorksheets();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, syncSlides);
    window.addEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, syncWorksheets);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, syncSlides);
      window.removeEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, syncWorksheets);
    };
  }, []);
  React.useEffect(() => {
    if (!isActive) return;
    setProgramProgressDirty(programLeaveDirty);
  }, [isActive, programLeaveDirty, setProgramProgressDirty]);
  React.useEffect(() => {
    if (!isActive) return undefined;
    saveProgramProgressRef.current = { save: handleSave, discard: handleDiscardUnsaved };
    return () => {
      saveProgramProgressRef.current = null;
    };
  }, [handleDiscardUnsaved, handleSave, isActive, saveProgramProgressRef]);

  const emptyProgramTableCard = (
    <ProgramProgressEmptyStateTableCard
      mode="fixed"
      toolbarVariant="fixed"
      academicYear={academicYear}
    />
  );

  return (
    <section>
      <div className="cp-program-page-header">
        <h2>
          {classParsed ? classParsed.title : '클래스 미선택'} — 고정 프로그램
          {termNumber ? (
            <>
              {' '}
              <span className="cp-program-page-term-pill">{termNumber}학기</span>
            </>
          ) : null}
        </h2>
        <div className="cp-program-page-save-actions">
          <ProgramToolbarUndoRedoButtons
            canUndo={programUndoRedoAvail.canUndo}
            canRedo={programUndoRedoAvail.canRedo}
            onUndo={() => {
              programUndoRedoApiRef.current?.undo();
            }}
            onRedo={() => {
              programUndoRedoApiRef.current?.redo();
            }}
          />
          <button
            type="button"
            className="cp-btn-save-all"
            onClick={handleSave}
            disabled={!programSavePending}
          >
            {programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
          </button>
        </div>
      </div>
      {!effectiveGradeId ? (
        <>
          <p className="page-desc">연간진도표에서 클래스를 선택하면 이 화면이 채워집니다.</p>
          {emptyProgramTableCard}
        </>
      ) : !termNumber ? (
        <>
          <p className="page-desc">왼쪽 고정 프로그램에서 1학기 또는 2학기 메뉴를 선택해 주세요.</p>
          {emptyProgramTableCard}
        </>
      ) : error ? (
        <p className="page-desc" role="alert">{error}</p>
      ) : showTableLoading ? (
        <p className="page-desc">불러오는 중…</p>
      ) : !selectedEntry ? (
        <>{emptyProgramTableCard}</>
      ) : !termDef ? (
        <>
          <p className="page-desc">해당 학기의 학사일정이 없습니다.</p>
          {emptyProgramTableCard}
        </>
      ) : !hasDisplayTableContent ? (
        <>
          <p className="page-desc">해당 학기에는 고정 프로그램 색으로 입력된 시간표 일정이 없습니다.</p>
          {emptyProgramTableCard}
        </>
      ) : (
        <>
          {rows.length === 0 ? (
            <p className="page-desc">
              해당 학기 연간 시간표에 고정 프로그램 일정이 아직 없습니다. 프로그램 내용은 미리 입력할 수 있으며,
              연간에 일정이 생기면 같은 순서의 행과 맞춰집니다.
            </p>
          ) : null}
          <ProgramWorkspaceStaleConflictBanner
            visible={fixedScopeServerSync.staleConflict}
            onAcceptServer={fixedScopeServerSync.acceptServerRefresh}
            onDismiss={fixedScopeServerSync.dismissStaleConflict}
          />
          <article
          className="cp-card cp-weekly-class-hours-card cp-program-progress-page-table-card"
        >
          <div className="cp-weekly-class-hours-controls cp-program-progress-table-card-toolbar">
            <div className="cp-program-progress-table-card-toolbar-left">
              <label>
                학년도
                <span className="cp-academic-year-readonly" aria-label={`학년도 ${academicYear}학년도(학사일정 기준 고정)`}>
                  {academicYear}학년도
                </span>
              </label>
            </div>
            <ProgramTableDocumentExportButtons
              onSaveExcel={handleProgramDocumentSaveExcel}
              onSavePdf={handleProgramDocumentSavePdf}
              onPrint={handleProgramDocumentPrint}
              disabled={!canExportProgramDocument}
              busy={programDocumentExportBusy}
            />
          </div>
            <ProgramProgressTable
              key={`${fixedScopeId}::${programTableMountKey}`}
              rows={displayTableRows}
              mode="fixed_to_unit"
              workspaceId={workspaceId}
              academicYear={academicYear}
              termNumber={termNumber}
              focusAnnualCellKey={focusAnnualCellKeyFromUrl || null}
              programCellsByAnnualKey={displayProgramCellsByAnnualKey}
              saveMode="fixed_content"
              onUpsertFixedContentRows={upsertCells}
              onBindSaveAction={handleBindSaveAction}
              onDirtyChange={setHasUnsavedChanges}
              workspaceScopeId={fixedScopeId}
              onWorkspaceRowsChange={handleWorkspaceRowsChange}
              onRemovedFixedWorkspaceRows={handleRemovedFixedWorkspaceRows}
              onEnsureWorkspaceSnapshot={handleEnsureWorkspaceSnapshot}
              classScheduleSectionRows={selectedEntry?.sectionRows ?? []}
              registeredTimetableNames={registeredTimetableNames}
              registeredTimetableWildcardChars={registeredTimetableWildcardChars}
              scheduleSlotAnnualCellKeys={displayScheduleSlotAnnualCellKeys}
              programUndoRedoApiRef={programUndoRedoApiRef}
              onProgramUndoRedoAvailabilityChange={setProgramUndoRedoAvail}
              programDataCleanupGradeId={effectiveGradeId}
              programDataCleanupIncludeFixedRows
              onProgramDataCleanupComplete={() => setSaveRefreshRev((v) => v + 1)}
            />
        </article>
        </>
      )}
      <button
        type="button"
        className="cp-fab-save"
        onClick={() => {
          void handleSave();
        }}
        disabled={!programSavePending}
        aria-label={programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
        title={programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
      >
        <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
      </button>
      {saveToastState ? (
        <div className="cp-toast-auto" role="status" aria-live="polite">
          <div className={`cp-toast-auto-box cp-toast-auto-box-${saveToastState.kind}`}>
            {saveToastState.kind === 'saving' ? (
              <span className="cp-toast-auto-spinner" aria-hidden="true" />
            ) : saveToastState.kind === 'success' ? (
              <span className="cp-toast-auto-success-icon" aria-hidden="true">
                v
              </span>
            ) : null}
            <span>{saveToastState.message}</span>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ProgramFixedPage;
